import React, { useEffect, useState } from 'react';
import './SkipNavigation.css';

/**
 * SkipNavigation component for keyboard navigation accessibility
 * Based on ArbiWin design system and WCAG guidelines
 *
 * @param {Object} props - SkipNavigation properties
 * @param {Array} props.links - Skip navigation links array
 * @param {string} props.position - Position: 'top-left', 'top-center', 'top-right'
 * @param {boolean} props.showOnFocus - Whether to show only on focus
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} SkipNavigation component
 */
const SkipNavigation = function SkipNavigation({
  links = [],
  position = 'top-left',
  showOnFocus = true,
  className = '',
  testId
}) {

  const [isVisible, setIsVisible] = useState(!showOnFocus);
  const [currentFocus, setCurrentFocus] = useState(-1);

  const defaultLinks = [
    {
      id: 'main-content',
      label: 'Прескочи до основното съдържание',
      target: '#main-content',
      key: 'M'
    },
    {
      id: 'main-navigation',
      label: 'Прескочи до навигацията',
      target: '#main-navigation',
      key: 'N'
    },
    {
      id: 'search',
      label: 'Прескочи до търсенето',
      target: '#search',
      key: 'S'
    },
    {
      id: 'footer',
      label: 'Прескочи до подножието',
      target: '#footer',
      key: 'F'
    }
  ];

  const skipLinks = links.length > 0 ? links : defaultLinks;

  const skipClasses = [
    'arbiwin-skip-navigation',
    'arbiwin-skip-navigation--' + position,
    showOnFocus ? 'arbiwin-skip-navigation--focus-only' : '',
    isVisible ? 'arbiwin-skip-navigation--visible' : '',
    className
  ].filter(Boolean).join(' ');

  const announceSkip = function(target) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'arbiwin-sr-only';
    announcement.textContent = 'Прескочихте до ' + target.replace('#', '');
    
    document.body.appendChild(announcement);
    
    setTimeout(function() {
      document.body.removeChild(announcement);
    }, 1000);
  };

  const skipToTarget = function(target) {
    const targetElement = document.querySelector(target);
    if (targetElement) {
      // Make target focusable if it isn't already
      if (!targetElement.hasAttribute('tabindex')) {
        targetElement.setAttribute('tabindex', '-1');
      }

      // Focus and scroll to target
      targetElement.focus();
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });

      // Announce the skip action to screen readers
      announceSkip(target);
    }
  };

  // Handle keyboard navigation within skip links
  useEffect(function() {
    const handleKeyDown = function(event) {
      if (!isVisible) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setCurrentFocus(function(prev) {
            return prev < skipLinks.length - 1 ? prev + 1 : 0;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setCurrentFocus(function(prev) {
            return prev > 0 ? prev - 1 : skipLinks.length - 1;
          });
          break;
        case 'Home':
          event.preventDefault();
          setCurrentFocus(0);
          break;
        case 'End':
          event.preventDefault();
          setCurrentFocus(skipLinks.length - 1);
          break;
        case 'Escape':
          event.preventDefault();
          setIsVisible(false);
          setCurrentFocus(-1);
          break;
        default:
          break;
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      return function() {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isVisible, skipLinks.length]);

  // Handle keyboard shortcuts for skip navigation
  useEffect(function() {
    const handleGlobalKeyDown = function(event) {
      // Alt + S to show skip navigation
      if (event.altKey && event.key.toLowerCase() === 's') {
        event.preventDefault();
        setIsVisible(true);
        setCurrentFocus(0);
        return;
      }

      // Individual skip link shortcuts (Alt + key)
      if (event.altKey) {
        const link = skipLinks.find(function(link) {
          return link.key && link.key.toLowerCase() === event.key.toLowerCase();
        });

        if (link) {
          event.preventDefault();
          skipToTarget(link.target);
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return function() {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [skipLinks]);

  const handleLinkClick = function(event, link) {
    event.preventDefault();
    skipToTarget(link.target);
    setIsVisible(false);
    setCurrentFocus(-1);
  };

  const handleLinkFocus = function(index) {
    setCurrentFocus(index);
    if (showOnFocus) {
      setIsVisible(true);
    }
  };

  const handleLinkBlur = function() {
    // Small delay to allow focus to move to another skip link
    setTimeout(function() {
      const activeElement = document.activeElement;
      const isSkipLink = activeElement && activeElement.closest('.arbiwin-skip-navigation');
      
      if (!isSkipLink && showOnFocus) {
        setIsVisible(false);
        setCurrentFocus(-1);
      }
    }, 100);
  };

  return (
    <nav
      className={skipClasses}
      role="navigation"
      aria-label="Навигация за прескачане"
      data-testid={testId || 'arbiwin-skip-navigation'}
    >
      <ul className="arbiwin-skip-navigation__list">
        {skipLinks.map(function(link, index) {
          const isActive = currentFocus === index;
          
          return (
            <li key={link.id} className="arbiwin-skip-navigation__item">
              <a
                href={link.target}
                className={[
                  'arbiwin-skip-navigation__link',
                  isActive ? 'arbiwin-skip-navigation__link--active' : ''
                ].filter(Boolean).join(' ')}
                onClick={function(event) {
                  handleLinkClick(event, link);
                }}
                onFocus={function() {
                  handleLinkFocus(index);
                }}
                onBlur={handleLinkBlur}
                aria-describedby={link.key ? link.id + '-shortcut' : undefined}
              >
                {link.label}
                {link.key && (
                  <span
                    id={link.id + '-shortcut'}
                    className="arbiwin-skip-navigation__shortcut"
                    aria-label={'Клавишна комбинация: Alt + ' + link.key}
                  >
                    Alt + {link.key}
                  </span>
                )}
              </a>
            </li>
          );
        })}
      </ul>

      {/* Instructions for users */}
      <div className="arbiwin-skip-navigation__instructions arbiwin-sr-only">
        Използвайте клавишите със стрелки за навигация, Enter за избор, или Escape за затваряне.
        Натиснете Alt + S за показване на навигацията за прескачане.
      </div>
    </nav>
  );
};

/**
 * SkipLink component - Individual skip link
 *
 * @param {Object} props - SkipLink properties
 * @param {string} props.href - Target element selector
 * @param {string} props.children - Link text
 * @param {string} props.shortcut - Keyboard shortcut key
 * @param {Function} props.onClick - Click handler
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} SkipLink component
 */
const SkipLink = function SkipLink({
  href,
  children,
  shortcut,
  onClick,
  className = ''
}) {

  const handleClick = function(event) {
    event.preventDefault();
    
    if (onClick) {
      onClick(event);
      return;
    }

    const targetElement = document.querySelector(href);
    if (targetElement) {
      if (!targetElement.hasAttribute('tabindex')) {
        targetElement.setAttribute('tabindex', '-1');
      }
      
      targetElement.focus();
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <a
      href={href}
      className={'arbiwin-skip-link ' + className}
      onClick={handleClick}
    >
      {children}
      {shortcut && (
        <span className="arbiwin-skip-link__shortcut">
          Alt + {shortcut}
        </span>
      )}
    </a>
  );
};

/**
 * useSkipNavigation hook for programmatic skip navigation
 *
 * @returns {Object} Skip navigation utilities
 */
const useSkipNavigation = function() {
  const [skipTargets] = useState(new Map());

  const registerSkipTarget = function(id, element, label) {
    if (element) {
      skipTargets.set(id, { element, label });
      
      // Ensure element is focusable
      if (!element.hasAttribute('tabindex')) {
        element.setAttribute('tabindex', '-1');
      }
    }
  };

  const unregisterSkipTarget = function(id) {
    skipTargets.delete(id);
  };

  const skipTo = function(id) {
    const target = skipTargets.get(id);
    if (target && target.element) {
      target.element.focus();
      target.element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });

      // Announce to screen readers
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'arbiwin-sr-only';
      announcement.textContent = 'Прескочихте до ' + target.label;
      
      document.body.appendChild(announcement);
      setTimeout(function() {
        document.body.removeChild(announcement);
      }, 1000);
    }
  };

  const getSkipTargets = function() {
    return Array.from(skipTargets.entries()).map(function([id, target]) {
      return {
        id,
        label: target.label,
        element: target.element
      };
    });
  };

  return {
    registerSkipTarget,
    unregisterSkipTarget,
    skipTo,
    getSkipTargets
  };
};

SkipNavigation.displayName = 'SkipNavigation';
SkipLink.displayName = 'SkipLink';

export default SkipNavigation;
export { SkipLink, useSkipNavigation };
